import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import ApiService from '../../services/api';
import { useNavigation } from '../../contexts/index';
import * as API from '../../types/api';
import './index.css'; // Import CSS for styling
import { ClassroomRatioTimeline, ClassroomRatioTimeSegment } from '../../../../server/src/types/classroom';
import TimelineBar from './TimelineBar';
import TimeMarker from './TimeMarker'; // Import the new TimeMarker component
import IndividualTimeline, { PersonTimeline, PersonTimeSegment } from './IndividualTimeline'; // Import the new component
import { ShiftRecord } from '../../../../server/src/types/whenIWork';
import StudentCountTimeline from './StudentCountTimeline'; // Import the new StudentCountTimeline component
import ZoomMenu from './ZoomMenu'; // <-- Import the new ZoomMenu component
import { useClassroomData, processedClassroomData } from '../../data/classroomData';

interface ClassDeailtedData {
  [classId: string]: {
    teachers: PersonTimeline[];
    students: PersonTimeline[];
  }
}

interface HistoricalRatiosProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const HistoricalRatios: React.FC<HistoricalRatiosProps> = ({refreshTrigger}) => {
  // loading and error states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add state to track expanded classrooms
  const [expandedClassrooms, setExpandedClassrooms] = useState<Record<string, boolean>>({});
  
  // Add state to track navigation targets
  const [targetNavigation, setTargetNavigation] = useState<{
    classroomId: string | null;
    teacherId: string | null;
  }>({ classroomId: null, teacherId: null });
  
  // References to classroom elements for scrolling
  const classroomRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const teacherTimelineRefs = useRef<Record<string, Record<string, HTMLDivElement | null>>>({});

  // Add a ref for the "Other" section container
  const otherSectionRef = useRef<HTMLDivElement | null>(null);
  
  // Add a mapping of teacher IDs to their timeline elements in the "Other" section
  const otherSectionTeacherRefs = useRef<Record<string, HTMLDivElement | null>>({});

  //data states
  const [timelineData, setTimelineData] = useState<API.GetClassroomRatiosOverTimeReturn>();  //timeline data from server
  const [classDetailedData, setClassDetailedData] = useState<Record<string, PersonTimeline[]>>({});  //calculated detail timeline data

  // input states
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); 
  const [timeRange, setTimeRange] = useState<{start: Date, end: Date}>({ 
    start: setTimeToHour(new Date(), 6), // 6:00 AM
    end: setTimeToHour(new Date(), 19)   // 7:00 PM
  });
  
  // zoom state
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const timeAxisRef = useRef<HTMLDivElement>(null);
  const latestRequestIdRef = useRef<number>(0);
  // Add a ref to store the padding width
  const paddingWidthRef = useRef<number>(160); // Initial fallback value
  
  // Use processedClassroomData for sections and classroomsInfo
  const classroomData = useClassroomData().data || {};
  const { sections } = useMemo(()=> processedClassroomData(classroomData), [classroomData]);

  // Add state for storing zoom parameters
  const [zoomParams, setZoomParams] = useState<{
    cursorPosRatio: number | null;
    mouseX: number | null;
    oldWidth: number | null;
    oldScroll: number | null;
  }>({ cursorPosRatio: null, mouseX: null, oldWidth: null, oldScroll: null });  
  
  // Highlight overtime state
  const [highlightOvertime, setHighlightOvertime] = useState(false);
  const [overtimeBuffer, setOvertimeBuffer] = useState(0); // in minutes

  // Toggle function for classroom expansion
  const toggleClassroomExpansion = (classroomId: string) => {
    setExpandedClassrooms(prev => ({
      ...prev,
      [classroomId]: !prev[classroomId]
    }));
  };

  // --- Add expand/collapse all handlers ---
  const handleExpandAll = () => {
    if (!timelineData) return;
    const allIds: Record<string, boolean> = {};
    timelineData.timeline.forEach(c => { allIds[c.classId] = true; });
    setExpandedClassrooms(allIds);
  };
  const handleCollapseAll = () => {
    if (!timelineData) return;
    const allIds: Record<string, boolean> = {};
    timelineData.timeline.forEach(c => { allIds[c.classId] = false; });
    setExpandedClassrooms(allIds);
  };
  // --- End expand/collapse all handlers ---

  // Helper function to set time to a specific hour
  function setTimeToHour(date: Date, hour: number): Date {
    const newDate = new Date(date);
    newDate.setHours(hour, 0, 0, 0);
    return newDate;
  }

  // Organize timeline data by sections following the order in sections
  const organizeTimelineBySection = () => {
    if (!timelineData || !sections) return [];
    
    // Create a map of classroomId -> section
    const classroomToSectionMap: Record<string, string> = {};
    sections.forEach(section => {
      section.classrooms.forEach(classroom => {
        classroomToSectionMap[classroom.id] = section.name;
      });
    })  

    // Timelines grouped by section
    const sectionTimelines: Record<string, Array<typeof timelineData.timeline[0]>> = {};

    // Initialize with empty arrays for all sections to preserve section order
    sections.forEach(section => {
      sectionTimelines[section.name] = [];
    });
    
    // Fill with timeline data
    timelineData.timeline.forEach(classroom => {
      const sectionName = classroomToSectionMap[classroom.classId] || 'Other';
      if (!sectionTimelines[sectionName]) sectionTimelines[sectionName] = []
      sectionTimelines[sectionName].push(classroom);
    });
    
    // For each section, sort classrooms according to their order in the section
    Object.keys(sectionTimelines).forEach(sectionName => {
      const section = sections.find(s => s.name === sectionName);
      if (section) {
        // Create a map of classroom ID to its order in the section
        const classroomOrderMap: Record<string, number> = {};
        section.classrooms.forEach((classroom, index) => {
          classroomOrderMap[classroom.id] = index;
        });
        
        // Sort the timeline data for this section based on classroom order
        sectionTimelines[sectionName].sort((a, b) => {
          return (classroomOrderMap[a.classId] - classroomOrderMap[b.classId]);
        });
      }
    });
    
    // Return data in the same order as sections in context
    return sections.map(section => ({
      sectionName: section.name,
      classrooms: sectionTimelines[section.name] || []
    }));
  };

  // Generate aggregated timeline for a section
  const generateSectionAggregatedTimeline = (classrooms: Array<ClassroomRatioTimeline>):ClassroomRatioTimeSegment[] => {
    if (!classrooms || classrooms.length === 0) return [];
    
    // Step 1: Collect all time points where changes occur
    const timePoints = new Set<number>();
    
    // Add all start and end times to the set
    classrooms.forEach(classroom => {
      classroom.timeSegments.forEach(segment => {
        timePoints.add(new Date(segment.startTime).getTime());
        timePoints.add(new Date(segment.endTime).getTime());
      });
    });
    
    // Convert set to array and sort
    const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
    
    if (sortedTimePoints.length < 2) return []; // Need at least 2 time points to create segments
    
    // Step 2: Create segments between each consecutive pair of time points
    const segments: {
      startTime: Date,
      endTime: Date,
      studentIds: string[],
      activeTeacherIds: string[],
      teachersOnBreakIds: string[],
      classroomCount: number
    }[] = [];
    
    for (let i = 0; i < sortedTimePoints.length - 1; i++) {
      const segmentStart = new Date(sortedTimePoints[i]);
      const segmentEnd = new Date(sortedTimePoints[i + 1]);
      
      // Skip segments with no duration
      if (segmentStart.getTime() === segmentEnd.getTime()) continue;
      
      // For this time interval, find which classroom segments are active
      const activeStudentIds = new Set<string>();
      const activeTeacherIds = new Set<string>();
      const teachersOnBreakIds = new Set<string>();
      let activeClassroomCount = 0;
      
      classrooms.forEach(classroom => {
        // Check if any segments in this classroom are active during this time interval
        let isClassroomActive = false;
        
        classroom.timeSegments.forEach(segment => {
          const segmentStartTime = new Date(segment.startTime).getTime();
          const segmentEndTime = new Date(segment.endTime).getTime();
          
          // If this classroom segment is active during our time interval
          if (segmentStartTime <= segmentStart.getTime() && segmentEndTime >= segmentEnd.getTime()) {
            // This segment completely encompasses our time interval
            segment.studentIds.forEach(id => activeStudentIds.add(id));
            segment.activeTeacherIds.forEach(id => activeTeacherIds.add(id));
            segment.teachersOnBreakIds.forEach(id => teachersOnBreakIds.add(id));
            isClassroomActive = true;
          } else if (segmentStartTime <= segmentStart.getTime() && segmentEndTime > segmentStart.getTime()) {
            // This segment starts before and ends during our interval
            segment.studentIds.forEach(id => activeStudentIds.add(id));
            segment.activeTeacherIds.forEach(id => activeTeacherIds.add(id));
            segment.teachersOnBreakIds.forEach(id => teachersOnBreakIds.add(id));
            isClassroomActive = true;
          } else if (segmentStartTime < segmentEnd.getTime() && segmentEndTime >= segmentEnd.getTime()) {
            // This segment starts during and ends after our interval
            segment.studentIds.forEach(id => activeStudentIds.add(id));
            segment.activeTeacherIds.forEach(id => activeTeacherIds.add(id));
            segment.teachersOnBreakIds.forEach(id => teachersOnBreakIds.add(id));
            isClassroomActive = true;
          } else if (segmentStartTime >= segmentStart.getTime() && segmentEndTime <= segmentEnd.getTime()) {
            // This segment is completely contained within our interval
            segment.studentIds.forEach(id => activeStudentIds.add(id));
            segment.activeTeacherIds.forEach(id => activeTeacherIds.add(id));
            segment.teachersOnBreakIds.forEach(id => teachersOnBreakIds.add(id));
            isClassroomActive = true;
          }
        });
        
        if (isClassroomActive) {
          activeClassroomCount++;
        }
      });
      
      // Create a segment for this time interval
      segments.push({
        startTime: segmentStart,
        endTime: segmentEnd,
        studentIds: Array.from(activeStudentIds),
        activeTeacherIds: Array.from(activeTeacherIds),
        teachersOnBreakIds: Array.from(teachersOnBreakIds),
        classroomCount: activeClassroomCount
      });
    }
    
    // Step 3: Merge adjacent segments with identical occupancy to simplify the timeline
    if (segments.length <= 1) return segments;
    
    const mergedSegments: typeof segments = [];
    let currentSegment = segments[0];
    
    for (let i = 1; i < segments.length; i++) {
      const nextSegment = segments[i];
      
      // Check if the sets of IDs are identical
      const currentStudentIds = new Set(currentSegment.studentIds);
      const nextStudentIds = new Set(nextSegment.studentIds);
      const currentTeacherIds = new Set(currentSegment.activeTeacherIds);
      const nextTeacherIds = new Set(nextSegment.activeTeacherIds);
      const currentBreakTeacherIds = new Set(currentSegment.teachersOnBreakIds);
      const nextBreakTeacherIds = new Set(nextSegment.teachersOnBreakIds);
      
      const sameStudents = currentStudentIds.size === nextStudentIds.size && 
        [...currentStudentIds].every(id => nextStudentIds.has(id));
      const sameTeachers = currentTeacherIds.size === nextTeacherIds.size && 
        [...currentTeacherIds].every(id => nextTeacherIds.has(id));
      const sameBreakTeachers = currentBreakTeacherIds.size === nextBreakTeacherIds.size && 
        [...currentBreakTeacherIds].every(id => nextBreakTeacherIds.has(id));
      
      if (sameStudents && sameTeachers && sameBreakTeachers && 
          currentSegment.classroomCount === nextSegment.classroomCount) {
        // Same occupancy, extend the current segment
        currentSegment.endTime = nextSegment.endTime;
      } else {
        // Different occupancy, push the current segment and start a new one
        mergedSegments.push(currentSegment);
        currentSegment = nextSegment;
      }
    }
    
    // Push the last segment
    mergedSegments.push(currentSegment);
    
    return mergedSegments;
  };

  // Helper function to merge adjacent segments with the same status
  const mergeAdjacentSegments = (segments: PersonTimeSegment[]): PersonTimeSegment[] => {
    if (segments.length <= 1) return segments;
    
    // Sort segments by start time
    const sortedSegments = [...segments].sort((a, b) => 
      a.startTime.getTime() - b.startTime.getTime()
    );
    
    const mergedSegments: PersonTimeSegment[] = [];
    let currentSegment = sortedSegments[0];
    
    for (let i = 1; i < sortedSegments.length; i++) {
      const nextSegment = sortedSegments[i];
      
      // If current segment ends when next segment starts and statuses match
      if (
        currentSegment.endTime.getTime() === nextSegment.startTime.getTime() &&
        currentSegment.status === nextSegment.status
      ) {
        // Extend current segment
        currentSegment.endTime = nextSegment.endTime;
      } else {
        // Push current segment and move to next
        mergedSegments.push(currentSegment);
        currentSegment = nextSegment;
      }
    }
    
    // Push the last segment
    mergedSegments.push(currentSegment);
    
    return mergedSegments;
  };

  // Helper function to consolidate segments that are close to each other in time
  const bridgeCloseSegments = (segments: PersonTimeSegment[], maxGapDuration: number): PersonTimeSegment[] => {
    if (segments.length <= 1) return segments;
    
    // Sort segments by start time
    const sortedSegments = [...segments].sort((a, b) => 
      a.startTime.getTime() - b.startTime.getTime()
    );
    
    const consolidatedSegments: PersonTimeSegment[] = [];
    let currentSegment = sortedSegments[0];
    
    for (let i = 1; i < sortedSegments.length; i++) {
      const nextSegment = sortedSegments[i];
      
      // If current segment and next segment have the same status and the gap is small enough
      if (
        currentSegment.status === nextSegment.status &&
        nextSegment.startTime.getTime() - currentSegment.endTime.getTime() <= maxGapDuration
      ) {
        // Bridge the gap by extending current segment to the end of next segment
        currentSegment.endTime = new Date(Math.max(
          currentSegment.endTime.getTime(),
          nextSegment.endTime.getTime()
        ));
      } else {
        // Gap too large or different status, can't bridge
        consolidatedSegments.push(currentSegment);
        currentSegment = nextSegment;
      }
    }
    
    // Push the last segment
    consolidatedSegments.push(currentSegment);
    
    return consolidatedSegments;
  };

  // Function to generate individual timelines for teachers and students in a classroom
  const generateIndividualTimelines = (classroom: ClassroomRatioTimeline) => {
    if (!timelineData) return { teacherTimelines: [], studentTimelines: [] };
    
    // Define minimum segment duration (30 seconds in milliseconds)
    const MIN_SEGMENT_DURATION = 30 * 1000;
    // Define maximum gap to bridge (30 seconds in milliseconds)
    const MAX_GAP_DURATION = 30 * 1000;
    
    const teacherTimelines: PersonTimeline[] = [];
    const studentTimelines: PersonTimeline[] = [];
    
    // Process teachers
    const teacherIds = new Set<string>();
    classroom.timeSegments.forEach(segment => {
      segment.activeTeacherIds.forEach(id => teacherIds.add(id));
      segment.teachersOnBreakIds.forEach(id => teacherIds.add(id));
    });
    
    teacherIds.forEach(teacherId => {
      const teacher = timelineData.teachers.find(t => t.guid === teacherId);
      if (!teacher) return;
      
      const name = `${teacher.firstName} ${teacher.lastName}`;
      
      // Build a complete timeline of all teacher segments across all classrooms
      type TeacherPresence = {
        startTime: Date;
        endTime: Date;
        classroomId: string;
        classroomName: string;
        status: 'present' | 'break';
      };
      
      const allClassroomSegments: TeacherPresence[] = [];
      
      // Collect ALL classroom segments for this teacher from the entire timeline data
      timelineData.timeline.forEach(classroomData => {
        classroomData.timeSegments.forEach(segment => {
          const isActive = segment.activeTeacherIds.includes(teacherId);
          const isOnBreak = segment.teachersOnBreakIds.includes(teacherId);
          
          if (isActive || isOnBreak) {
            allClassroomSegments.push({
              startTime: new Date(segment.startTime),
              endTime: new Date(segment.endTime),
              classroomId: classroomData.classId,
              classroomName: classroomData.className,
              status: isOnBreak ? 'break' : 'present'
            });
          }
        });
      });
      
      // Sort all segments by start time to find earliest and latest
      allClassroomSegments.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      
      // Process segments for current classroom
      const segments: PersonTimeSegment[] = [];
      
      // For each time segment in the classroom timeline
      classroom.timeSegments.forEach(segment => {
        const isActive = segment.activeTeacherIds.includes(teacherId);
        const isOnBreak = segment.teachersOnBreakIds.includes(teacherId);
        
        if (isActive || isOnBreak) {
          const startTime = new Date(segment.startTime);
          const endTime = new Date(segment.endTime);
          
          segments.push({
            startTime,
            endTime,
            status: isOnBreak ? 'break' : 'present'
          });
        }
      });
      
      // First, bridge segments that are close in time with the same status
      const bridgedSegments = bridgeCloseSegments(segments, MAX_GAP_DURATION);
      
      // Then, filter out segments that are too short
      const filteredSegments = bridgedSegments.filter(segment => 
        segment.endTime.getTime() - segment.startTime.getTime() >= MIN_SEGMENT_DURATION
      );
      
      // Finally, merge adjacent segments with the same status (this handles exact adjacency)
      const mergedSegments = mergeAdjacentSegments(filteredSegments);
      
      // Add classroom transition info
      mergedSegments.forEach(segment => {
        // Find previous classroom
        const prevClassroom = allClassroomSegments.find(cs => 
          Math.abs(cs.endTime.getTime() - segment.startTime.getTime()) < 5 * 60 * 1000 && // Within 5 minutes
          cs.endTime.getTime() <= segment.startTime.getTime()
        );
        
        // Find next classroom
        const nextClassroom = allClassroomSegments.find(cs => 
          Math.abs(cs.startTime.getTime() - segment.endTime.getTime()) < 5 * 60 * 1000 && // Within 5 minutes
          cs.startTime.getTime() >= segment.endTime.getTime()
        );
        
        if (prevClassroom && (prevClassroom.classroomId !== classroom.classId)) {
          segment.prevClassroom = {
            id: prevClassroom.classroomId,
            name: prevClassroom.classroomName,
            status: prevClassroom.status
          };
        }
        
        if (nextClassroom && (nextClassroom.classroomId !== classroom.classId)) {
          segment.nextClassroom = {
            id: nextClassroom.classroomId,
            name: nextClassroom.classroomName,
            status: nextClassroom.status
          };
        }
      });
      
      // Add the teacher's shift information if available
      const wiw_shifts = timelineData.teachers.find(t => t.guid === teacherId)?.shifts || [];

      // Calculate shift markers for this teacher
      let shiftMarkers
      
      if (wiw_shifts && wiw_shifts.length > 0) {
        // Use actual shift data if available
        shiftMarkers = {
          starts: wiw_shifts.map(shift => ({
            time: new Date(shift.start_time),
            isEstimated: false
          })),
          ends: wiw_shifts.map(shift => ({
            time: new Date(shift.end_time),
            isEstimated: false
          }))
        };
      } else if (allClassroomSegments.length > 0) {
        // Use first segment from ANY classroom as shift start
        const firstSegment = allClassroomSegments[0];
        const lastSegment = allClassroomSegments[allClassroomSegments.length - 1];
        const now = new Date();
        
        // For end time: determine if teacher is currently in any classroom
        const isStillPresent = allClassroomSegments.some(segment => 
          segment.endTime > now || 
          (now.getTime() - segment.endTime.getTime()) < 30 * 60 * 1000 // Within last 30 minutes
        );
        
        let endTime;
        if (isStillPresent) {
          // Teacher is still present, use 30 minutes from now
          endTime = new Date(now.getTime() + 30 * 60 * 1000);
        } else {
          // Teacher is not present, use last segment's end time
          endTime = lastSegment.endTime;
        }
        
        shiftMarkers = {
          starts: [{
            time: firstSegment.startTime,
            isEstimated: true
          }],
          ends: [{
            time: endTime,
            isEstimated: true
          }]
        };
      }
      
      // Only add teacher if they have valid segments after processing
      if (mergedSegments.length > 0) {
        teacherTimelines.push({
          id: teacherId,
          name,
          role: 'teacher',
          timeSegments: mergedSegments,
          wiw_shift: wiw_shifts,
          shiftMarkers: shiftMarkers
        });
      }
    });
    
    // Process students with the same approach
    const studentIds = new Set<string>();
    classroom.timeSegments.forEach(segment => {
      segment.studentIds.forEach(id => studentIds.add(id));
    });
    
    studentIds.forEach(studentId => {
      const student = timelineData.students.find(s => s.id === studentId);
      if (!student) return;
      
      const segments: PersonTimeSegment[] = [];
      
      classroom.timeSegments.forEach(segment => {
        const isPresent = segment.studentIds.includes(studentId);
        
        if (isPresent) {
          segments.push({
            startTime: new Date(segment.startTime),
            endTime: new Date(segment.endTime),
            status: 'present'
          });
        }
      });
      
      // Apply the same processing steps as for teachers
      const bridgedSegments = bridgeCloseSegments(segments, MAX_GAP_DURATION);
      const filteredSegments = bridgedSegments.filter(segment => 
        segment.endTime.getTime() - segment.startTime.getTime() >= MIN_SEGMENT_DURATION
      );
      const mergedSegments = mergeAdjacentSegments(filteredSegments);
      
      // Only add student if they have valid segments after processing
      if (mergedSegments.length > 0) {
        studentTimelines.push({
          id: studentId,
          name: `${student.firstName} ${student.lastName}`,
          role: 'student',
          timeSegments: mergedSegments
        });
      }
    });
    
    return { teacherTimelines, studentTimelines };
  };

  const {activeTab} = useNavigation(); // Get active tab from context
  useEffect(() => {
    // Update time range when selected date changes
    const start = setTimeToHour(selectedDate, 6);
    const end = setTimeToHour(selectedDate, 19);
    setTimeRange({ start, end });
  }, [selectedDate, refreshTrigger]);

  useEffect(() => {
    fetchHistoricalData();
  }, [timeRange, activeTab]);

  const fetchHistoricalData = async () => {
    if (activeTab !== 'historical') return // Only run when this tab is active
    
    // Generate a unique request ID
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setIsLoading(true);  
      const data = await ApiService.getClassroomRatiosOverTime(
        timeRange.start.toISOString(),
        timeRange.end.toISOString()
      );
      
      // Only update state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setTimelineData(data);
        setError(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      
      // Only update error state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setError(errorMessage);
      }
    } finally {
      // Only update loading state if this is still the latest request
      if (latestRequestIdRef.current === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Add effect to handle Alt + scroll wheel for zooming
  useEffect(() => {
    const timelineElement = timelineContainerRef.current;
    const scrollContainer = timelineElement?.parentElement;

    if (!timelineElement || !scrollContainer) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.altKey) {
        event.preventDefault();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Calculate absolute mouse X (within timeline)
        const absoluteMouseX = (event.clientX - containerRect.left) + scrollContainer.scrollLeft;
        const oldWidth = timelineElement.offsetWidth;
        const ratio = absoluteMouseX / oldWidth; 

        setZoomLevel(prevZoomLevel => {
          let newZoomLevel = prevZoomLevel;
          if (event.deltaY < 0) {
            newZoomLevel = Math.min(prevZoomLevel + 0.005 * -event.deltaY, 50);
          } else {
            newZoomLevel = Math.max(prevZoomLevel - 0.005 * event.deltaY, 1);
          }
          if (newZoomLevel !== zoomLevel) {
            setZoomParams({
              cursorPosRatio: ratio,
              mouseX: (event.clientX - containerRect.left), // mouse X relative to container
              oldWidth,
              oldScroll: scrollContainer.scrollLeft
            });
          }
          return newZoomLevel;
        });
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [timelineContainerRef.current]);

  useLayoutEffect(() => {
    const timelineElement = timelineContainerRef.current;
    const scrollContainer = timelineElement?.parentElement;

    if (!scrollContainer || !timelineElement ||
        zoomParams.cursorPosRatio === null ||
        zoomParams.mouseX === null ||
        zoomParams.oldWidth === null ||
        zoomParams.oldScroll === null) {
      return;
    }

    const newWidth = timelineElement.offsetWidth;
    const oldWidth = zoomParams.oldWidth;

    if (newWidth !== oldWidth) {
      // Use the dynamically calculated padding instead of hardcoded value
      const padding = paddingWidthRef.current;
      const newScrollLeft =
        (zoomParams.oldScroll*(newWidth-padding) + (padding-zoomParams.mouseX)*(oldWidth-newWidth))/(oldWidth-padding);
      scrollContainer.scrollLeft = newScrollLeft;
    }

    setZoomParams({ cursorPosRatio: null, mouseX: null, oldWidth: null, oldScroll: null });
  }, [zoomLevel, zoomParams]); // Run when zoom level or params change

  // Handle transition click from IndividualTimeline component
  const handleTransitionClick = (classroomId: string, teacherId: string) => {
    // Check if the classroom is in the "Other" section
    const isOtherSectionClassroom = classroomId.startsWith('other-') || 
      (timelineData && !timelineData.timeline.some(classroom => classroom.classId === classroomId));
    
    if (isOtherSectionClassroom) {
      // For "Other" section, we only need to track the teacher ID
      setTargetNavigation({ classroomId: 'other-section', teacherId });
    } else {
      // For regular classrooms, proceed as before
      setExpandedClassrooms(prev => ({
        ...prev,
        [classroomId]: true
      }));
      
      // Set navigation target
      setTargetNavigation({ classroomId, teacherId });
    }
  };

  // Listen for show/hide events
  useEffect(() => {
    const handleShowClassroomTimelineDeatils = (event: CustomEvent) => {
      //get id from data-classroom-id attribute
      const target = event?.target as HTMLElement | null;
      const id = target?.dataset?.classroomId;
      console.log(id)
      if (id) {
        setExpandedClassrooms(prev => ({
          ...prev,
          [id]: true
        }));
      }
    };
    const handleHideClassroomTimelineDeatils = (event: CustomEvent) => {
      //get id from data-classroom-id attribute
      const target = event?.target as HTMLElement | null;
      const id = target?.dataset?.classroomId;
      if (id) {
        setExpandedClassrooms(prev => ({
          ...prev,
          [id]: false
        }));
      }
    };

    window.addEventListener('tour-show', handleShowClassroomTimelineDeatils as EventListener);
    window.addEventListener('tour-hide', handleHideClassroomTimelineDeatils as EventListener);
    
    return () => {
      window.removeEventListener('tour-show', handleShowClassroomTimelineDeatils as EventListener);
      window.removeEventListener('tour-hide', handleHideClassroomTimelineDeatils as EventListener);
    };
  });
  
  // Effect to handle navigation and highlighting
  useEffect(() => {
    const { classroomId, teacherId } = targetNavigation;
    
    if (!classroomId) return;
    
    // Special handling for "Other" section
    if (classroomId === 'other-section') {
      // Scroll to the "Other" section container
      if (otherSectionRef.current) {
        otherSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }
      
      // After a brief delay, scroll to the specific teacher timeline if available
      const timeoutId = setTimeout(() => {
        if (teacherId && otherSectionTeacherRefs.current[teacherId]) {
          otherSectionTeacherRefs.current[teacherId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
        
        // Reset navigation target after animation completes
        const resetId = setTimeout(() => {
          setTargetNavigation({ classroomId: null, teacherId: null });
        }, 3000);
        
        return () => clearTimeout(resetId);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      // Regular classroom handling
      const classroomElement = classroomRefs.current[classroomId];
      if (classroomElement) {
        classroomElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }
      
      // Wait for classroom expansion and rendering
      const timeoutId = setTimeout(() => {
        // Then scroll to specific teacher timeline if available
        if (teacherId && classroomId && teacherTimelineRefs.current[classroomId]?.[teacherId]) {
          const teacherElement = teacherTimelineRefs.current[classroomId][teacherId];
          if (teacherElement) {
            teacherElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
        
        // Reset navigation target after a delay
        const resetId = setTimeout(() => {
          setTargetNavigation({ classroomId: null, teacherId: null });
        }, 3000); // Reset after animation completes
        
        return () => clearTimeout(resetId);
      }, 300); // Wait for expansion to complete
      
      return () => clearTimeout(timeoutId);
    }
  }, [targetNavigation]);

  // Helper to initialize refs structure
  const initTeacherRefs = (classroomId: string) => {
    if (!teacherTimelineRefs.current[classroomId]) {
      teacherTimelineRefs.current[classroomId] = {};
    }
  };

  // Helper to determine if a teacher timeline is overtime
  const isTeacherOvertime = (teacher: PersonTimeline) => {
    if (!highlightOvertime) return false;
    if (!teacher.shiftMarkers || !teacher.shiftMarkers.ends?.length) return false;
    // Find the latest shift end
    const maxShiftEnd = teacher.shiftMarkers.ends.reduce(
      (max, marker) => marker.time > max ? marker.time : max,
      teacher.shiftMarkers.ends[0].time
    );
    // If any segment ends after the shift end + buffer, highlight
    const bufferMs = overtimeBuffer * 60 * 1000;
    return teacher.timeSegments.some(seg => seg.endTime.getTime() > (maxShiftEnd.getTime() + bufferMs) && seg.status === 'present');
  };

  const renderTimeline = () => {
    if (!timelineData) return null;

    // Calculate the time span based on the current timeRange
    const startHour = timeRange.start.getHours();
    const endHour = timeRange.end.getHours();
    const totalHours = endHour - startHour;
    const totalMinutes = totalHours * 60;

    // Generate hour marks for the time axis
    const hourMarks = [];
    for (let i = 0; i < totalHours; i++) {
      const hour = startHour + i;
      const label = hour < 12 ? `${hour}am` : hour === 12 ? `${hour}pm` : `${hour - 12}pm`;
      hourMarks.push({ hour, label });
    }

    // Calculate width based on zoom level
    const timelineWidth = `${100 * zoomLevel}%`;
    
    // Get organized section timelines
    const sectionTimelines = organizeTimelineBySection();

    return (
      <div className="timeline-scroll-container">
        {/* Replace the marker line with the TimeMarker component */}
        <TimeMarker 
          timelineContainerRef={timelineContainerRef}
          timeAxisRef={timeAxisRef}
          timeRange={timeRange}
        />
        <div 
          className="timeline-container" 
          ref={timelineContainerRef}
          style={{ width: timelineWidth }}
        >
          <div className="time-axis" ref={timeAxisRef}>
            {hourMarks.map((mark, i) => (
              <div key={i} className="time-label">
                {mark.label}
              </div>
            ))}
          </div>
          
          {sectionTimelines.map((section, sectionIndex) => {
            const isOtherSection = section.sectionName.toLowerCase() === 'other';
            
            return (
              <div key={section.sectionName} 
                   className={`section-container ${isOtherSection ? 'other-section-container' : ''}`}
                   ref={isOtherSection ? otherSectionRef : null}>
                <div className="section-header">
                  <div className="section-name-container">
                    <span>{section.sectionName}</span>
                  </div>
                  
                  {/* Only show aggregate timeline for non-Other sections */}
                  {!isOtherSection && (
                    <TimelineBar
                      segments={generateSectionAggregatedTimeline(section.classrooms)}
                      timeRange={timeRange}
                      teachers={timelineData.teachers}
                      students={timelineData.students}
                      requiredRatio={sections[sectionIndex]?.requiredRatio}
                    />
                  )}
                </div>
                
                {/* For "Other" section, directly show teacher timelines */}
                {isOtherSection ? (
                  <div className="other-teachers-container">
                    {(() => {
                      // Collect teachers from all classrooms in the Other section
                      const officeTeacherTimelines: PersonTimeline[] = [];
                      const floaterTeacherTimelines: PersonTimeline[] = [];
                      
                      section.classrooms.forEach(classroom => {
                        const { teacherTimelines } = generateIndividualTimelines(classroom);
                        teacherTimelines.forEach(teacher => {
                          // Determine if teacher is Office or Floater based on classroom name
                          // You might need to adjust this logic based on your actual data
                          if (classroom.className.toLowerCase().includes('office')) {
                            // Check if this teacher is already in our list
                            if (!officeTeacherTimelines.some(t => t.id === teacher.id)) {
                              officeTeacherTimelines.push(teacher);
                            }
                          } else {
                            // Assumed to be a Floater if not in Office
                            if (!floaterTeacherTimelines.some(t => t.id === teacher.id)) {
                              floaterTeacherTimelines.push(teacher);
                            }
                          }
                        });
                      });

                      const renderTeacherGroup = (teachers: PersonTimeline[], title: string) => {
                        if (teachers.length === 0) return null;
                        
                        return (
                          <div className="teacher-group">
                            <h4 className="timeline-section-header">{title} ({teachers.length})</h4>
                            <div className="individual-timelines-section">
                              {teachers.map(teacher => {
                                // Check if this teacher should be highlighted
                                const isHighlighted = (targetNavigation.classroomId === 'other-section' && 
                                                      targetNavigation.teacherId === teacher.id) ||
                                                      isTeacherOvertime(teacher);
                                
                                return (
                                  <IndividualTimeline 
                                    key={teacher.id} 
                                    person={teacher} 
                                    timeRange={timeRange} 
                                    onTransitionClick={handleTransitionClick}
                                    highlightRef={(el) => {
                                      // Store the ref for this teacher's timeline
                                      otherSectionTeacherRefs.current[teacher.id] = el
                                    }}
                                    isHighlighted={isHighlighted}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      };
                      
                      return (
                        <div>
                          {renderTeacherGroup(officeTeacherTimelines, "Office")}
                          {renderTeacherGroup(floaterTeacherTimelines, "Floaters")}
                          {officeTeacherTimelines.length === 0 && floaterTeacherTimelines.length === 0 && (
                            <div className="empty-timelines">No teachers in Other section</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  // For regular sections, render classrooms as usual
                  section.classrooms.map((classroom) => (
                    <React.Fragment key={classroom.classId}>
                      <div 
                        className="classroom-row"
                        ref={(el) => classroomRefs.current[classroom.classId] = el}
                        onDoubleClick={(e) => {
                          e.preventDefault(); 
                          e.stopPropagation(); // Stop event from bubbling up
                          toggleClassroomExpansion(classroom.classId);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="classroom-name">
                          <span>{classroom.className}</span>
                          <button
                            data-classroom-id={classroom.classId}
                            className={`expand-button ${expandedClassrooms[classroom.classId] ? 'expanded' : ''}`}
                            onClick={() => toggleClassroomExpansion(classroom.classId)}
                            title={expandedClassrooms[classroom.classId] ? "Hide individual timelines" : "Show individual timelines"}
                          >
                            {expandedClassrooms[classroom.classId] ? '−' : '+'}
                          </button>
                        </div>
                        <TimelineBar
                          requiredRatio={classroomData[classroom.classId]?.requiredRatio || 10}
                          segments={classroom.timeSegments}
                          teachers={timelineData.teachers}
                          students={timelineData.students}
                          timeRange={timeRange}
                        />
                      </div>
                      
                      {expandedClassrooms[classroom.classId] && (
                        <div className="individual-timelines-container">
                          {/* Add StudentCountTimeline here, above other timelines */}
                          <StudentCountTimeline 
                            segments={classroom.timeSegments}
                            timeRange={timeRange}
                          />
                          
                          {(() => {
                            const { teacherTimelines, studentTimelines } = generateIndividualTimelines(classroom);
                            return (
                              <>
                                {teacherTimelines.length > 0 && (
                                  <div className="individual-timelines-section">
                                    {teacherTimelines.map(teacher => {
                                      // Initialize the refs structure for this classroom if needed
                                      initTeacherRefs(classroom.classId);
                                      
                                      // Check if this teacher should be highlighted
                                      const isHighlighted = (targetNavigation.classroomId === classroom.classId && 
                                                             targetNavigation.teacherId === teacher.id) ||
                                                             isTeacherOvertime(teacher);
                                                           
                                      // Create a ref for this teacher's timeline
                                      const timelineRef = React.createRef<HTMLDivElement>();
                                      
                                      // Store the ref for later use
                                      teacherTimelineRefs.current[classroom.classId][teacher.id] = timelineRef.current;
                                      
                                      return (
                                        <IndividualTimeline 
                                          key={teacher.id} 
                                          person={teacher} 
                                          timeRange={timeRange}
                                          onTransitionClick={handleTransitionClick}
                                          highlightRef={timelineRef}
                                          isHighlighted={isHighlighted}
                                        />
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {studentTimelines.length > 0 && (
                                  <div className="individual-timelines-section">
                                    <h4 className="timeline-section-header">Students ({studentTimelines.length})</h4>
                                    {studentTimelines.map(student => (
                                      <IndividualTimeline 
                                        key={student.id} 
                                        person={student} 
                                        timeRange={timeRange} 
                                      />
                                    ))}
                                  </div>
                                )}
                                
                                {teacherTimelines.length === 0 && studentTimelines.length === 0 && (
                                  <div className="empty-timelines">No individual data available</div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </React.Fragment>
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(new Date(`${event.target.value}T00:00:00`));
  };

  // Add navigation functions for previous and next day
  const handlePreviousDay = () => {
    const prevDay = new Date(selectedDate);
    do {
      prevDay.setDate(prevDay.getDate() - 1);
    } while (prevDay.getDay() === 0 || prevDay.getDay() === 6);
    setSelectedDate(prevDay);
  };

  const handleNextDay = () => {
    const nextDay = new Date(selectedDate);
    do {
      nextDay.setDate(nextDay.getDate() + 1);
    } while (nextDay.getDay() === 0 || nextDay.getDay() === 6);
    setSelectedDate(nextDay);
  };

  // Check if the next day button should be visible
  const getNextWorkday = (date: Date): Date => {
    const next = new Date(date);
    do {
      next.setDate(next.getDate() + 1);
    } while (next.getDay() === 0 || next.getDay() === 6);
    return next;
  };

  const isNextDayButtonVisible = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const nextWorkday = getNextWorkday(selectedDate);
    return nextWorkday.getTime() <= today.getTime();
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 5)); // Corrected max zoom to 5x
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1)); // Limit min zoom to 0.5x
  };

  const handleZoomReset = () => {
    setZoomLevel(1); // Reset to default zoom
  };

  // Format date to display the day of week and date
  const formatDateWithDayOfWeek = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  return (
    <div className="historical-ratios-component">
      <div className="controls-container">
        <div className="date-selector">
          <button 
        className="date-nav-button" 
        onClick={handlePreviousDay}
        title="Previous Day"
          >
        <span className="nav-icon">&#10094;</span>
          </button>
          
          <div className="date-input-container">
        <label htmlFor="date">Select Date: </label>
        <input
          type="date"
          id="date"
          value={selectedDate.toISOString().split('T')[0]}
          onChange={handleDateChange}
          onClick={(e) => e.currentTarget.showPicker()}
          onTouchEnd={(e) => e.currentTarget.showPicker()}
          autoComplete="off"
        />
          </div>
          
          {isNextDayButtonVisible() && (
        <button 
          className="date-nav-button" 
          onClick={handleNextDay}
          title="Next Day"
        >
          <span className="nav-icon">&#10095;</span>
        </button>
          )}
        </div>
        
        {/* New date header showing day of week and formatted date */}
        <div className="date-header">
          {formatDateWithDayOfWeek(selectedDate)}
        </div>
        
        {/* --- Use imported ZoomMenu component --- */}
        <div className="zoom-controls" style={{ background: 'none', padding: 0 }}>
          <div className="control-hints-container">
            <span className="control-hint">(Hold Alt + Scroll to zoom)</span>
            <span className="control-hint">(Hold Shift to scroll horizontally)</span>
          </div>
          <ZoomMenu
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            highlightOvertime={highlightOvertime}
            onToggleHighlightOvertime={() => setHighlightOvertime(v => !v)}
            overtimeBuffer={overtimeBuffer}
            onOvertimeBufferChange={setOvertimeBuffer}
          />
        </div>
        {/* --- End replacement --- */}
      </div>
      
      {/* Timeline wrapper with loading class when isLoading is true */}
      <div className={`timeline-wrapper ${isLoading ? 'loading' : ''}`}>
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
        {error && <p className="error-message">Error: {error}</p>}
        {timelineData && renderTimeline()}
        {!timelineData && !isLoading && <p>No data available. Please select a different date.</p>}
      </div>
    </div>
  );
};

export default HistoricalRatios;